import { useContext, useEffect, useRef } from 'react'
import * as Config from '../config'
import * as Context from '../context'

/**
 * This hook is used to evaluate a trigger when a screen unmounts.
 *
 * @param trigger - The trigger to evaluate.
 * @param arePreconditionsMet - If specified, allows the trigger to be evaluated
 *  only if the preconditions are met. Otherwise, the trigger will be evaluated
 *  when the component unmounts.
 *
 * @example
 * ```tsx
 * useScreenUnmountTrigger({
 *   trigger: 'rewards_referral_code_copied',
 *   arePreconditionsMet: wasCopyButtonPressed,
 * })
 * ```
 */
export const useScreenUnmountTrigger = (trigger: Config.Trigger, arePreconditionsMet = true) => {
  const { evaluateTrigger } = useContext(Context.SurveysContext)
  const arePreconditionsMetRef = useRef(arePreconditionsMet)
  const triggerRef = useRef(trigger)
  const evaluateTriggerRef = useRef(evaluateTrigger)

  // Update the refs when the props change.
  arePreconditionsMetRef.current = arePreconditionsMet
  triggerRef.current = trigger
  evaluateTriggerRef.current = evaluateTrigger

  // Evaluate the trigger when the component unmounts.
  // Use refs to avoid re-evaluating the trigger when the props change.
  useEffect(function evaluateTriggerOnUnmount() {
    return () => {
      if (arePreconditionsMetRef.current) {
        void evaluateTriggerRef.current(triggerRef.current)
      }
    }
  }, [])
}
